import { createReducer, Reducer } from "@reduxjs/toolkit";
import { Pageable } from "../../services/pageable";
import { RemoteResource } from "../common/fetchable";
import {
  EmptyFetchable,
  Fetchable,
  isFetchableError,
  isFetchableSuccess,
} from "../common/fetchable/helpers";
import { WalletsActions } from "../wallets/actions";
import { UserActions } from "./actions";
import { UsersDTO } from "./models/dto";

export namespace Users {
  export const current: RemoteResource<
    UsersDTO.Responses.GetSingle.T,
    UsersDTO.Responses.GetSingle.T
  > = {
    fetch: UserActions.getUserDetails,
    updateStore: UserActions.setUserDetails,
    selector: (store) => store.users.current,
  };

  export const allUsers: RemoteResource<
    UsersDTO.Responses.GetAll.T,
    UsersDTO.Responses.GetAll.T,
    Pageable.T<keyof UsersDTO.Responses.GetSingle.T>
  > = {
    fetch: UserActions.setPagination,
    updateStore: UserActions.setUsers,
    selector: (store) => store.users.allUsers.data,
  };

  export const singleUser: RemoteResource<
    UsersDTO.Responses.GetSingle.T,
    UsersDTO.Responses.GetSingle.T,
    {
      userId: UsersDTO.Responses.GetSingle.T["userReferenceId"];
      refetch?: boolean;
    }
  > = {
    fetch: UserActions.getSingleUser,
    updateStore: UserActions.setSingleUser,
    selector: (store) => store.users.singleUser,
  };
}

/**
 * Interface used for Redux Store
 */
export interface Users {
  current: Fetchable<UsersDTO.Responses.GetSingle.T>;
  error?: Error;
  allUsers: {
    data: Fetchable<UsersDTO.Responses.GetAll.T>;
    pagination: Pageable.T<keyof UsersDTO.Responses.GetSingle.T>;
  };
  allUsersMap: Record<string, UsersDTO.Responses.GetSingle.T | "LOADING">;
  singleUser: Fetchable<UsersDTO.Responses.GetSingle.T>;
}

// Reducer
export const usersReducer: Reducer<Users, UserActions.Actions> = createReducer(
  {
    current: EmptyFetchable,
    allUsers: {
      data: EmptyFetchable,
      pagination: {},
    },
    allUsersMap: {},
    singleUser: EmptyFetchable,
    allMerchants: {
      resource: EmptyFetchable,
      options: {},
    },
    allMerchantsMap: {},
    singleMerchant: EmptyFetchable,
  } as Users,
  (builder) => {
    builder
      .addCase(UserActions.setUserDetails, (state, action) => {
        if (isFetchableError(action.payload)) {
          state.current = action.payload;
        } else {
          state.current = {
            status: "ready",
            data: action.payload.data,
          };
        }
      })
      .addCase(UserActions.resetUserDetails, (state) => {
        state.current = EmptyFetchable;
      })
      .addCase(UserActions.getUserDetails, (state) => {
        state.current = {
          status: "loading",
        };
      })
      .addCase(UserActions.setUsers, (state, action) => {
        //TODO: use id as link to map
        state.allUsers.data = action.payload;
        if (action.payload.status === "ready") {
          action.payload.data.content.forEach((p) => {
            state.allUsersMap[p.userReferenceId] = p;
          });
        }
      })
      .addCase(UserActions.resetUsers, (state) => {
        state.allUsers.data = EmptyFetchable;
        state.allUsers.pagination = {};
        state.allUsersMap = {};
        state.singleUser = EmptyFetchable;
      })
      .addCase(UserActions.setSingleUser, (state, action) => {
        state.singleUser = action.payload;
        if (isFetchableSuccess(action.payload)) {
          state.allUsersMap[action.payload.data.userReferenceId] =
            action.payload.data;
        }
      })
      .addCase(UserActions.getUsers, (state) => {
        state.allUsers.data = {
          status: "loading",
        };
      })
      .addCase(WalletsActions.setLoyalty, (state, action) => {
        if (isFetchableSuccess(state.current)) {
          state.current.data = { ...state.current.data, ...action.payload };
        }
      })
      .addCase(UserActions.setPagination, (state, action) => {
        state.allUsers.pagination = action.payload;
        state.allUsers.data = {
          status: "loading",
        };
      });
  }
);
